import React, { useState } from 'react';
import { PlusIcon, EditIcon, TrashIcon, ToggleLeftIcon, ToggleRightIcon, SearchIcon, MailIcon } from 'lucide-react';
import { InboundEmailConfig as InboundEmailConfigType } from './EmailConfiguration';
import { InboundEmailForm } from './InboundEmailForm';
interface InboundEmailConfigProps {
  inboundConfigs: InboundEmailConfigType[];
  setInboundConfigs: React.Dispatch<React.SetStateAction<InboundEmailConfigType[]>>;
  locale: string;
}
export function InboundEmailConfig({
  inboundConfigs,
  setInboundConfigs,
  locale
}: InboundEmailConfigProps) {
  const [showForm, setShowForm] = useState(false);
  const [editingConfig, setEditingConfig] = useState<InboundEmailConfigType | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const handleAddConfig = () => {
    setEditingConfig(null);
    setShowForm(true);
  };
  const handleEditConfig = (config: InboundEmailConfigType) => {
    setEditingConfig(config);
    setShowForm(true);
  };
  const handleToggleActive = (id: string) => {
    setInboundConfigs(inboundConfigs.map(config => config.id === id ? {
      ...config,
      isActive: !config.isActive
    } : config));
  };
  const handleDeleteConfig = (id: string) => {
    if (confirm('Are you sure you want to delete this email configuration? This action cannot be undone.')) {
      setInboundConfigs(inboundConfigs.filter(config => config.id !== id));
    }
  };
  const handleSaveConfig = (config: Partial<InboundEmailConfigType>) => {
    if (editingConfig) {
      // Update existing config
      setInboundConfigs(inboundConfigs.map(c => c.id === editingConfig.id ? {
        ...c,
        ...config,
        updatedAt: new Date().toISOString()
      } : c));
    } else {
      // Create new config
      const newConfig: InboundEmailConfigType = {
        id: `inbound-${Date.now()}`,
        scopeLevel: config.scopeLevel || 'Tenant',
        entityId: config.entityId,
        entityName: config.entityName,
        siteId: config.siteId,
        siteName: config.siteName,
        emailAddress: config.emailAddress || '',
        imapHost: config.imapHost || '',
        imapPort: config.imapPort || 993,
        useSSL: config.useSSL !== undefined ? config.useSSL : true,
        username: config.username || '',
        password: config.password || '',
        pollingFrequencyMinutes: config.pollingFrequencyMinutes || 15,
        defaultDocumentType: config.defaultDocumentType || 'Invoice',
        defaultTags: config.defaultTags || [],
        acceptEmailBodyAsAttachment: config.acceptEmailBodyAsAttachment || false,
        isActive: config.isActive !== undefined ? config.isActive : true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        rules: []
      };
      setInboundConfigs([...inboundConfigs, newConfig]);
    }
    setShowForm(false);
    setEditingConfig(null);
  };
  const handleTestConnection = (id: string) => {
    // In a real application, this would make an API call to test the connection
    alert('Connection test initiated. This would test the connection to the mail server in a real application.');
  };
  const filteredConfigs = inboundConfigs.filter(config => config.emailAddress.toLowerCase().includes(searchTerm.toLowerCase()) || config.entityName && config.entityName.toLowerCase().includes(searchTerm.toLowerCase()) || config.siteName && config.siteName.toLowerCase().includes(searchTerm.toLowerCase()));
  const getScopeLabel = (config: InboundEmailConfigType) => {
    switch (config.scopeLevel) {
      case 'Tenant':
        return 'Tenant-wide';
      case 'Entity':
        return `Entity: ${config.entityName}`;
      case 'Site':
        return `Site: ${config.siteName}`;
      default:
        return 'Unknown';
    }
  };
  return <div className="space-y-6" data-id="element-17324">
      {showForm ? <InboundEmailForm config={editingConfig} onSave={handleSaveConfig} onCancel={() => {
      setShowForm(false);
      setEditingConfig(null);
    }} locale={locale} data-id="element-17325" /> : <>
          <div className="bg-white shadow-sm rounded-lg p-4" data-id="element-17326">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0" data-id="element-17327">
              <div className="flex-1 max-w-md" data-id="element-17328">
                <div className="relative rounded-md shadow-sm" data-id="element-17329">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-17330">
                    <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-17331" />
                  </div>
                  <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search email configurations..." data-id="element-17332" />
                </div>
              </div>
              <button onClick={handleAddConfig} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17333">
                <PlusIcon className="h-4 w-4 mr-2" data-id="element-17334" />
                Add Email Configuration
              </button>
            </div>
          </div>
          <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-17335">
            {filteredConfigs.length === 0 ? <div className="px-6 py-12 text-center" data-id="element-17336">
                <MailIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-17337" />
                <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-17338">
                  No email configurations
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-17339">
                  Get started by creating a new inbound email configuration.
                </p>
                <div className="mt-6" data-id="element-17340">
                  <button type="button" onClick={handleAddConfig} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17341">
                    <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" data-id="element-17342" />
                    Add Email Configuration
                  </button>
                </div>
              </div> : <table className="min-w-full divide-y divide-gray-200" data-id="element-17343">
                <thead className="bg-gray-50" data-id="element-17344">
                  <tr data-id="element-17345">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-17346">
                      Email Address
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-17347">
                      Scope
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-17348">
                      Server Details
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-17349">
                      Polling
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-17350">
                      Rules
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-17351">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-17352">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-17353">
                  {filteredConfigs.map(config => <tr key={config.id} className="hover:bg-gray-50" data-id="element-17354">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-17355">
                        {config.emailAddress}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-17356">
                        {getScopeLabel(config)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-17357">
                        {config.imapHost}:{config.imapPort}
                        <br data-id="element-17358" />
                        <span className="text-xs text-gray-400" data-id="element-17359">
                          {config.useSSL ? 'SSL Enabled' : 'SSL Disabled'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-17360">
                        Every {config.pollingFrequencyMinutes} minutes
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-17361">
                        {config.rules.length} rule(s)
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-17362">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${config.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-17363">
                          {config.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-17364">
                        <div className="flex justify-end space-x-2" data-id="element-17365">
                          <button onClick={() => handleTestConnection(config.id)} className="text-indigo-600 hover:text-indigo-900" title="Test Connection" data-id="element-17366">
                            <div className="h-4 w-4" data-id="element-17367" />
                          </button>
                          <button onClick={() => handleEditConfig(config)} className="text-indigo-600 hover:text-indigo-900" title="Edit" data-id="element-17368">
                            <EditIcon className="h-4 w-4" data-id="element-17369" />
                          </button>
                          <button onClick={() => handleToggleActive(config.id)} className={`${config.isActive ? 'text-green-600 hover:text-green-900' : 'text-gray-400 hover:text-gray-600'}`} title={config.isActive ? 'Deactivate' : 'Activate'} data-id="element-17370">
                            {config.isActive ? <ToggleRightIcon className="h-4 w-4" data-id="element-17371" /> : <ToggleLeftIcon className="h-4 w-4" data-id="element-17372" />}
                          </button>
                          <button onClick={() => handleDeleteConfig(config.id)} className="text-red-600 hover:text-red-900" title="Delete" data-id="element-17373">
                            <TrashIcon className="h-4 w-4" data-id="element-17374" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>}
          </div>
        </>}
    </div>;
}